import { FC, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	IconButton,
	LinearProgress,
	MenuItem,
	TablePagination,
	TextField,
	Typography,
} from '@mui/material';
import { ContentCopy, Refresh } from '@mui/icons-material';
import { TFilters, TOrderBy } from 'redux/types';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import {
	TPgLegacyMethodType,
	TPgLegacyTransaction,
	TPgLegacyTransactionKind,
	TPgLegacyTransactionPurpose,
	TPgLegacyTransactionStatus,
	TPgLegacyTransactionTrigger,
	TPgListRequest,
	TPgListResponse,
} from 'services/api/paymentGateway/types';
import { ESortDirection } from 'ui/EasTable/types';
import { EasTooltip } from 'ui/EasTooltip';
import { notificationContainer } from 'utils/notificationContainer';
import { copyText, useReloadKey } from '../common/helpers';
import { PgMiniTable, PgStatusChip, TPgMiniColumn } from '../common/PgMiniTable';
import { humanize } from '../common/tableHelpers';
import { PG_METHOD_TYPE_LABELS } from '../constants';
import { formatDateTime, formatMoney } from '../utils';

type TRow = TPgLegacyTransaction;

const KIND_LABELS: Record<TPgLegacyTransactionKind, string> = {
	payment: 'Payment',
	refund: 'Refund',
};
const PURPOSE_LABELS: Record<TPgLegacyTransactionPurpose, string> = {
	invoice: 'Invoice',
	eu_ddp_prepayment: 'EU DDP prepayment',
};
const STATUS_LABELS: Record<TPgLegacyTransactionStatus, string> = {
	succeeded: 'Succeeded',
	failed: 'Failed',
	cancelled: 'Cancelled',
	processing: 'Processing',
};
const TRIGGER_LABELS: Record<TPgLegacyTransactionTrigger, string> = {
	manual: 'Manual',
	auto: 'Auto',
};
const METHOD_LABELS: Record<TPgLegacyMethodType, string> = {
	...PG_METHOD_TYPE_LABELS,
	other: 'Other',
};

/** Label of an enum value; a value the client does not know yet is shown humanized. */
const labelOf = (labels: Record<string, string>, value: string | null | undefined): string =>
	(value && labels[value]) || humanize(value);

const PAGE_SIZES = [10, 25, 50];
const DEFAULT_ORDER: TOrderBy = { fieldName: 'occurredAt', direction: ESortDirection.DESC };

type TFilterState = {
	kind: string;
	purpose: string;
	methodType: string;
	status: string;
	invoiceNumber: string;
};
type TSelectFilter = Exclude<keyof TFilterState, 'invoiceNumber'>;

const NO_FILTERS: TFilterState = {
	kind: '',
	purpose: '',
	methodType: '',
	status: '',
	invoiceNumber: '',
};

const SELECT_FILTERS: { field: TSelectFilter; label: string; options: Record<string, string> }[] = [
	{ field: 'kind', label: 'Type', options: KIND_LABELS },
	{ field: 'purpose', label: 'Purpose', options: PURPOSE_LABELS },
	{ field: 'methodType', label: 'Method', options: METHOD_LABELS },
	{ field: 'status', label: 'Status', options: STATUS_LABELS },
];

/** Filters of the standard list request (`FilterParams` format, as the other gateway lists). */
const toListFilters = (filters: TFilterState): TFilters =>
	(Object.keys(filters) as (keyof TFilterState)[])
		.filter((field) => filters[field] !== '')
		.map((field) => ({
			fieldName: field,
			name: field,
			value: filters[field],
			operator: '=',
			comparator: '=',
		}));

const ExternalRef: FC<{ row: TRow }> = ({ row }) => {
	const copy = async () => {
		const copied = await copyText(row.externalRef);
		notificationContainer(
			copied ? 'Reference copied' : 'Copy failed: select the reference manually',
			copied ? 'success' : 'error',
		);
	};
	return (
		<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25 }}>
			<Box sx={{ minWidth: 0 }}>
				<Typography
					variant="caption"
					color="text.secondary"
					component="div"
					sx={{ wordBreak: 'break-all' }}
				>
					{row.externalRef}
				</Typography>
				{row.sourceOrderRef && (
					<Typography
						variant="caption"
						color="text.secondary"
						component="div"
						sx={{ wordBreak: 'break-all' }}
					>
						Order: {row.sourceOrderRef}
					</Typography>
				)}
			</Box>
			<EasTooltip title="Copy reference">
				<IconButton size="small" sx={{ p: 0.25 }} onClick={() => void copy()}>
					<ContentCopy sx={{ fontSize: 14 }} />
				</IconButton>
			</EasTooltip>
		</Box>
	);
};

const COLUMNS: TPgMiniColumn<TRow>[] = [
	{
		header: 'Date',
		sortField: 'occurredAt',
		render: (row) => <Box sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.occurredAt)}</Box>,
	},
	{ header: 'Type', sortField: 'kind', render: (row) => labelOf(KIND_LABELS, row.kind) },
	{ header: 'Purpose', render: (row) => labelOf(PURPOSE_LABELS, row.purpose) },
	{ header: 'Invoice', sortField: 'invoiceNumber', render: (row) => row.invoiceNumber ?? '—' },
	{ header: 'Method', render: (row) => row.label || labelOf(METHOD_LABELS, row.methodType) },
	{
		header: 'Status',
		sortField: 'status',
		render: (row) => {
			const chip = <PgStatusChip value={row.status} label={labelOf(STATUS_LABELS, row.status)} />;
			return row.rawStatus && row.rawStatus !== row.status ? (
				<EasTooltip title={`Status in the previous payment system: ${row.rawStatus}`}>
					<span>{chip}</span>
				</EasTooltip>
			) : (
				chip
			);
		},
	},
	{
		header: 'Amount',
		align: 'right',
		sortField: 'amount',
		render: (row) => (
			<Box sx={{ whiteSpace: 'nowrap' }}>
				{formatMoney(row.amount, row.currency)}
				{row.chargedCurrency && row.chargedCurrency !== row.currency && (
					<Typography variant="caption" color="text.secondary" component="div">
						≈ {formatMoney(row.chargedAmount, row.chargedCurrency)} charged
					</Typography>
				)}
			</Box>
		),
	},
	{
		header: 'Fee',
		align: 'right',
		render: (row) => <Box sx={{ whiteSpace: 'nowrap' }}>{formatMoney(row.fee, row.currency)}</Box>,
	},
	{ header: 'Trigger', render: (row) => labelOf(TRIGGER_LABELS, row.trigger) },
	{
		header: 'Failure reason',
		render: (row) =>
			row.failureReason ? (
				<EasTooltip title={row.failureReason}>
					<Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
						{row.failureReason}
					</Typography>
				</EasTooltip>
			) : (
				'—'
			),
	},
	{ header: 'External ref', render: (row) => <ExternalRef row={row} /> },
];

type TLoaded = {
	/** Request the result belongs to: a different one means a newer request is in flight. */
	key: string;
	response: TPgListResponse<TRow> | null;
	error: string | null;
};

type TCustomerLegacyTransactionsProps = {
	customerId: number;
};

/**
 * Customer card, tab "Previous payment system": payments and refunds imported from the payment
 * system used before the gateway. Read only; paging, sorting and filters are sent to the server
 * and kept in local state (not in the URL, which belongs to the customers list under the card).
 */
export const CustomerLegacyTransactions: FC<TCustomerLegacyTransactionsProps> = ({
	customerId,
}) => {
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(PAGE_SIZES[1]);
	const [sort, setSort] = useState<TOrderBy>(DEFAULT_ORDER);
	const [filters, setFilters] = useState<TFilterState>(NO_FILTERS);
	const [invoiceDraft, setInvoiceDraft] = useState('');
	const [reloadKey, reload] = useReloadKey();
	const [loaded, setLoaded] = useState<TLoaded | null>(null);

	const request = useMemo<TPgListRequest>(
		() => ({
			pageNumber: page + 1,
			pageSize,
			searchString: '',
			filters: toListFilters(filters),
			orderBy: sort.fieldName === DEFAULT_ORDER.fieldName ? [sort] : [sort, DEFAULT_ORDER],
		}),
		[page, pageSize, sort, filters],
	);
	const requestKey = `${customerId}:${reloadKey}:${JSON.stringify(request)}`;

	// State is set only when the response arrives; "loading" is derived from the request key.
	useEffect(() => {
		let active = true;
		api.paymentGateway
			.getCustomerLegacyTransactions({ customerId, body: request })
			.then((response) => {
				if (active) setLoaded({ key: requestKey, response, error: null });
			})
			.catch((e) => {
				if (active) {
					setLoaded({
						key: requestKey,
						response: null,
						error: getPgErrorMessage(e, 'Failed to load the payments of the previous system'),
					});
				}
			});
		return () => {
			active = false;
		};
	}, [customerId, request, requestKey]);

	const loading = loaded?.key !== requestKey;
	const response = loaded?.response ?? null;
	const total = response?.totalCount ?? 0;
	const hasFilters = Object.values(filters).some((value) => value !== '');

	const changeFilter = (field: keyof TFilterState, value: string) => {
		if (filters[field] === value) return;
		setFilters({ ...filters, [field]: value });
		setPage(0);
	};

	const clearFilters = () => {
		setInvoiceDraft('');
		if (!hasFilters) return;
		setFilters(NO_FILTERS);
		setPage(0);
	};

	const changeSort = (next: TOrderBy) => {
		setSort(next);
		setPage(0);
	};

	return (
		<Box>
			<Alert severity="info" sx={{ mb: 2 }}>
				Payments made before the switch to the current payment system. Receipts are not available
				for them.
			</Alert>

			<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
				{(hasFilters || total > 0) && (
					<>
						{SELECT_FILTERS.map(({ field, label, options }) => (
							<TextField
								key={field}
								select
								size="small"
								label={label}
								value={filters[field]}
								onChange={(e) => changeFilter(field, e.target.value)}
								sx={{ minWidth: 140 }}
							>
								<MenuItem value="">All</MenuItem>
								{Object.entries(options).map(([value, text]) => (
									<MenuItem key={value} value={value}>
										{text}
									</MenuItem>
								))}
							</TextField>
						))}
						<TextField
							size="small"
							label="Invoice"
							value={invoiceDraft}
							onChange={(e) => setInvoiceDraft(e.target.value.replace(/\D/g, ''))}
							onBlur={() => changeFilter('invoiceNumber', invoiceDraft)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') changeFilter('invoiceNumber', invoiceDraft);
							}}
							inputProps={{ inputMode: 'numeric' }}
							sx={{ width: 140 }}
						/>
						{(hasFilters || invoiceDraft) && (
							<Button size="small" onClick={clearFilters}>
								Clear filters
							</Button>
						)}
					</>
				)}
				<Box sx={{ flexGrow: 1 }} />
				<Button
					size="small"
					onClick={reload}
					disabled={loading}
					startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
				>
					Refresh
				</Button>
			</Box>

			{!loading && loaded?.error && (
				<Alert
					severity="error"
					action={
						<Button color="inherit" size="small" onClick={reload}>
							Retry
						</Button>
					}
				>
					{loaded.error}
				</Alert>
			)}
			{loading && !response && <CircularProgress size={24} />}
			{response && (
				<>
					<Box sx={{ height: 4 }}>{loading && <LinearProgress />}</Box>
					<PgMiniTable
						rows={response.data}
						getKey={(row) => row.id}
						columns={COLUMNS}
						sort={sort}
						onSortChange={changeSort}
						maxHeight="none"
						emptyText={
							hasFilters
								? 'No payments from the previous payment system match the filters.'
								: 'No payments from the previous payment system.'
						}
					/>
					{total > 0 && (
						<TablePagination
							component="div"
							count={total}
							page={page}
							rowsPerPage={pageSize}
							rowsPerPageOptions={PAGE_SIZES}
							onPageChange={(_event, next) => setPage(next)}
							onRowsPerPageChange={(e) => {
								setPageSize(Number(e.target.value));
								setPage(0);
							}}
						/>
					)}
				</>
			)}
		</Box>
	);
};
